import React, { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from 'sonner';
import { HelmetProvider } from 'react-helmet-async';
import { BrightLabsCmsPage } from './lib/BrightLabsCMS';
import MarketingSEO from './lib/MarketingSEO';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WhatsAppButton from './components/WhatsAppButton';
import TrackingScripts from './components/TrackingScripts';
import Home from './pages/Home';
import About from './pages/About';
import Membership from './pages/Membership';
import Schedule from './pages/Schedule';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import EliteResidency from './pages/EliteResidency';
import usePageTracking from './hooks/usePageTracking';
import { BlogList, BlogPost } from './pages/Blog';
import SEOPage from './pages/SEOPage';
import ProtectedRoute from './admin/ProtectedRoute';
import PortalProtectedRoute from './portal/PortalProtectedRoute';
import PortalErrorBoundary from './portal/PortalErrorBoundary';

// ── Lazy-loaded: standalone public pages (own full-screen chrome) ──
const LandingPage = lazy(() => import('./pages/LandingPage'));
const ThankYouPage = lazy(() => import('./pages/ThankYouPage'));
const InvestorPitch = lazy(() => import('./pages/InvestorPitch'));
const PCMCProposal = lazy(() => import('./pages/PCMCProposal'));
const CameraConfidence = lazy(() => import('./pages/CameraConfidence'));
const FoundingHundred = lazy(() => import('./pages/FoundingHundred'));
const CobraThaiKesnand = lazy(() => import('./pages/CobraThaiKesnand'));
const CobraThaiFatLoss = lazy(() => import('./pages/CobraThaiFatLoss'));
const FunnelThankYou = lazy(() => import('./pages/FunnelThankYou'));
const Unsubscribe = lazy(() => import('./pages/Unsubscribe'));
const FeedbackForm = lazy(() => import('./pages/FeedbackForm'));
const CoachFeedbackForm = lazy(() => import('./pages/CoachFeedbackForm'));
const CustomerWaiver = lazy(() => import('./CustomerWaiver'));
const TabletDisplay = lazy(() => import('./TabletDisplay'));
const SnakePitEventList = lazy(() => import('./pages/SnakePitPublic').then(m => ({ default: m.SnakePitEventList })));
const SnakePitEventDetail = lazy(() => import('./pages/SnakePitPublic').then(m => ({ default: m.SnakePitEventDetail })));
const FightCalendarPublic = lazy(() => import('./pages/FightCalendar'));

// ── Lazy-loaded: Admin (kept out of the public bundle) ──
const Login = lazy(() => import('./admin/Login'));
const EmergencyReset = lazy(() => import('./pages/EmergencyReset'));
const Dashboard = lazy(() => import('./admin/Dashboard'));
const Pages = lazy(() => import('./admin/Pages'));
const PageEditor = lazy(() => import('./admin/PageEditor'));
const Users = lazy(() => import('./admin/Users'));
const Analytics = lazy(() => import('./admin/Analytics'));
const WebsiteAnalytics = lazy(() => import('./admin/WebsiteAnalytics'));
const AmbassadorTracker = lazy(() => import('./admin/SocialEngagement'));
const SiteSettings = lazy(() => import('./admin/SiteSettings'));
const BlogManager = lazy(() => import('./admin/BlogManager'));
const BlogAnalytics = lazy(() => import('./admin/BlogAnalytics'));
const SEOPages = lazy(() => import('./admin/SEOPages'));
const SnakePit = lazy(() => import('./admin/SnakePit'));
const FollowUpCenter = lazy(() => import('./admin/FollowUpCenter'));
const Playbooks = lazy(() => import('./admin/Playbooks'));
const TrainingDecks = lazy(() => import('./admin/TrainingDecks'));
const OutstandingPayments = lazy(() => import('./admin/OutstandingPayments'));
const MacroBeastDashboard = lazy(() => import('./admin/macrobeast/MacroBeastDashboard'));
const MacroBeastSubscriptions = lazy(() => import('./admin/macrobeast/MacroBeastSubscriptions'));
const MacroBeastPOS = lazy(() => import('./admin/macrobeast/MacroBeastPOS'));
const MacroBeastExpenses = lazy(() => import('./admin/macrobeast/MacroBeastExpenses'));

// ── Lazy-loaded: CRM ──
const CRMDashboard = lazy(() => import('./admin/CRMDashboard'));
const CRMLeads = lazy(() => import('./admin/CRMLeads'));
const CRMMembers = lazy(() => import('./admin/CRMMembers'));
const CRMCampaigns = lazy(() => import('./admin/CRMCampaigns'));
const CRMLeadDatabase = lazy(() => import('./admin/CRMLeadDatabase'));
const CRMProducts = lazy(() => import('./admin/CRMProducts'));
const CRMCoaches = lazy(() => import('./admin/CRMCoaches'));
const CRMEmployees = lazy(() => import('./admin/CRMEmployees'));
const CRMLinks = lazy(() => import('./admin/CRMLinks'));
const CRMAttendance = lazy(() => import('./admin/CRMAttendance'));
const CRMPTSchedule = lazy(() => import('./admin/CRMPTSchedule'));
const FlexiPay = lazy(() => import('./admin/FlexiPay'));
const FlexiPayPlaybook = lazy(() => import('./admin/FlexiPayPlaybook'));
const SalesExecution = lazy(() => import('./admin/SalesExecution'));
const SalesDashboard = lazy(() => import('./admin/SalesDashboard'));
const ActivityCenter = lazy(() => import('./admin/ActivityCenter'));
const HRModule = lazy(() => import('./admin/HRModule'));
const HRCalendar = lazy(() => import('./admin/HRCalendar'));
const Payroll = lazy(() => import('./admin/Payroll'));
const Financials = lazy(() => import('./admin/Financials'));
const TaxPayroll = lazy(() => import('./admin/TaxPayroll'));
const CompanySettings = lazy(() => import('./admin/CompanySettings'));
const Locations = lazy(() => import('./admin/Locations'));
const OperatorIntelligence = lazy(() => import('./admin/OperatorIntelligence'));
const CustomerAnalytics = lazy(() => import('./admin/CustomerAnalytics'));
const Events = lazy(() => import('./admin/Events'));
const MyPortal = lazy(() => import('./admin/MyPortal'));
const CustomerFeedback = lazy(() => import('./admin/CustomerFeedback'));
const WhatsAppDashboard = lazy(() => import('./admin/WhatsAppDashboard'));
const CallAnalytics = lazy(() => import('./admin/CallAnalytics'));
const IceBath = lazy(() => import('./admin/IceBath'));
const PurchaseAnalytics = lazy(() => import('./admin/PurchaseAnalytics'));
const Reconciliation = lazy(() => import('./admin/Reconciliation'));
const FightCalendarAdmin = lazy(() => import('./admin/FightCalendar'));
const Inventory = lazy(() => import('./admin/Inventory'));
const AdvancedAnalytics = lazy(() => import('./admin/AdvancedAnalytics'));
const SpeedToLead = lazy(() => import('./admin/SpeedToLead'));
const ChurnManagement = lazy(() => import('./admin/ChurnManagement'));
const PhoneAudit = lazy(() => import('./admin/PhoneAudit'));
const DataHealthCheck = lazy(() => import('./admin/DataHealthCheck'));
const EmailAutomations = lazy(() => import('./admin/EmailAutomations'));
const Help = lazy(() => import('./pages/admin/Help'));
const EmployeeCheckin = lazy(() => import('./pages/employee/EmployeeCheckin'));
const TwoFactorSetup = lazy(() => import('./pages/settings/TwoFactorSetup'));
const MyLeaves = lazy(() => import('./pages/employee/MyLeaves'));
const TeamLeaveRequests = lazy(() => import('./pages/employee/TeamLeaveRequests'));
const AttendanceComparison = lazy(() => import('./pages/admin/AttendanceComparison'));
const RenewalSettings = lazy(() => import('./admin/RenewalSettings'));

// ── Lazy-loaded: Member Portal (PWA) ──
const PortalLogin = lazy(() => import('./portal/PortalLogin'));
const PortalLayout = lazy(() => import('./portal/PortalLayout'));
const PortalDashboard = lazy(() => import('./portal/PortalDashboard'));
const PortalRenewal = lazy(() => import('./portal/PortalRenewal'));
const PortalHistory = lazy(() => import('./portal/PortalHistory'));
const PortalHowItWorks = lazy(() => import('./portal/PortalHowItWorks'));
const PortalOnboarding = lazy(() => import('./portal/PortalOnboarding'));
const GamePlan = lazy(() => import('./portal/GamePlan'));
const ExerciseLibrary = lazy(() => import('./portal/ExerciseLibrary'));
const WeekBuilder = lazy(() => import('./portal/WeekBuilder'));
const Arcade = lazy(() => import('./portal/Arcade'));
const Skills = lazy(() => import('./portal/Skills'));
const StaffOnboarding = lazy(() => import('./portal/StaffOnboarding'));
const StaffTimeOff = lazy(() => import('./portal/StaffTimeOff'));
const StaffApprovals = lazy(() => import('./portal/StaffApprovals'));
const StaffPolicies = lazy(() => import('./portal/StaffPolicies'));
const StaffWorkClock = lazy(() => import('./portal/StaffWorkClock'));
const StaffFightCalendar = lazy(() => import('./portal/StaffFightCalendar'));
const TeamBenefits = lazy(() => import('./portal/TeamBenefits'));
const PortalGuide = lazy(() => import('./portal/PortalGuide'));
const CoachPTClients = lazy(() => import('./portal/CoachPTClients'));
const MemberPT = lazy(() => import('./portal/MemberPT'));

// Lightweight full-screen fallback shown while a code-split chunk loads
const PageLoader = () => (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000' }}>
    <div style={{ width: 40, height: 40, border: '3px solid #3f3f46', borderTopColor: '#dc2626', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// Scroll to top on route change, or to hash target if set
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    // Check if there's a scroll target from navigation
    const scrollTarget = sessionStorage.getItem('scrollTarget');
    if (scrollTarget) {
      sessionStorage.removeItem('scrollTarget');
      // Delay to allow page to render
      setTimeout(() => {
        const element = document.querySelector(scrollTarget);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  
  return null;
};

const AppContent = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isPortalRoute = location.pathname.startsWith('/portal');
  const [investorSlug, setInvestorSlug] = useState('investor-briefing-v1');
  const [slugLoaded, setSlugLoaded] = useState(false);

  // Register service worker for PWA (portal)
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    }
  }, []);

  // Fetch investor slug from backend (public endpoint)
  useEffect(() => {
    const fetchInvestorSlug = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${API_URL}/api/investor-settings/public`);
        if (res.ok) {
          const data = await res.json();
          setInvestorSlug(data.slug || 'investor-briefing-v1');
        }
      } catch (err) {
        // Use default slug
      } finally {
        setSlugLoaded(true);
      }
    };
    fetchInvestorSlug();
  }, []);

  // Track page views (skips admin routes internally)
  usePageTracking();

  // Member Portal routes (separate from admin)
  if (isPortalRoute) {
    return (
      <PortalErrorBoundary>
        <Routes>
          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal" element={<PortalProtectedRoute><PortalLayout /></PortalProtectedRoute>}>
            <Route index element={<PortalDashboard />} />
            <Route path="renew" element={<PortalRenewal />} />
            <Route path="history" element={<PortalHistory />} />
            <Route path="how-it-works" element={<PortalHowItWorks />} />
            <Route path="onboarding" element={<PortalOnboarding />} />
            <Route path="game-plan" element={<GamePlan />} />
            <Route path="game-plan/session/:sessionId" element={<GamePlan />} />
            <Route path="exercises" element={<ExerciseLibrary />} />
            <Route path="game-plan/build" element={<WeekBuilder />} />
            <Route path="arcade" element={<Arcade />} />
            <Route path="skills" element={<Skills />} />
            <Route path="staff/onboarding" element={<StaffOnboarding />} />
            <Route path="staff/time-off" element={<StaffTimeOff />} />
            <Route path="staff/approvals" element={<StaffApprovals />} />
            <Route path="staff/policies" element={<StaffPolicies />} />
            <Route path="staff/work" element={<StaffWorkClock />} />
            <Route path="fight-calendar" element={<StaffFightCalendar />} />
            <Route path="staff/benefits" element={<TeamBenefits />} />
            <Route path="guide" element={<PortalGuide />} />
            <Route path="coach/pt-clients" element={<CoachPTClients />} />
            <Route path="pt" element={<MemberPT />} />
          </Route>
          {/* Catch-all: redirect unknown /portal/* routes to portal home */}
          <Route path="/portal/*" element={<PortalProtectedRoute><PortalLayout /></PortalProtectedRoute>} />
        </Routes>
      </PortalErrorBoundary>
    );
  }

  // Don't show public navbar/footer on admin routes
  if (isAdminRoute) {
    return (
      <Routes>
        <Route path="/admin/login" element={<Login />} />
        <Route path="/admin/reset-password" element={<Login />} />
        <Route path="/admin/emergency-reset" element={<EmergencyReset />} />
        <Route path="/admin" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/admin/pages" element={<ProtectedRoute><Pages /></ProtectedRoute>} />
        <Route path="/admin/pages/new" element={<ProtectedRoute><PageEditor /></ProtectedRoute>} />
        <Route path="/admin/pages/:pageId/edit" element={<ProtectedRoute><PageEditor /></ProtectedRoute>} />
        <Route path="/admin/blog" element={<ProtectedRoute><BlogManager /></ProtectedRoute>} />
        <Route path="/admin/blog/analytics" element={<ProtectedRoute><BlogAnalytics /></ProtectedRoute>} />
        <Route path="/admin/seo-pages" element={<ProtectedRoute><SEOPages /></ProtectedRoute>} />
        <Route path="/admin/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
        <Route path="/admin/website-analytics" element={<ProtectedRoute><WebsiteAnalytics /></ProtectedRoute>} />
        <Route path="/admin/social-engagement" element={<ProtectedRoute><AmbassadorTracker /></ProtectedRoute>} />
        <Route path="/admin/settings" element={<ProtectedRoute><SiteSettings /></ProtectedRoute>} />
        <Route path="/admin/settings/company" element={<ProtectedRoute><CompanySettings /></ProtectedRoute>} />
        <Route path="/admin/settings/locations" element={<ProtectedRoute><Locations /></ProtectedRoute>} />
        
        {/* CRM Routes */}
        <Route path="/admin/crm" element={<ProtectedRoute><CRMDashboard /></ProtectedRoute>} />
        <Route path="/admin/crm/sales-execution" element={<ProtectedRoute><SalesExecution /></ProtectedRoute>} />
        <Route path="/admin/crm/my-dashboard" element={<ProtectedRoute><SalesDashboard /></ProtectedRoute>} />
        <Route path="/admin/crm/activity" element={<ProtectedRoute><ActivityCenter /></ProtectedRoute>} />
        <Route path="/admin/crm/whatsapp" element={<ProtectedRoute><WhatsAppDashboard /></ProtectedRoute>} />
        <Route path="/admin/crm/calls" element={<ProtectedRoute><CallAnalytics /></ProtectedRoute>} />
        <Route path="/admin/crm/leads" element={<ProtectedRoute><CRMLeads /></ProtectedRoute>} />
        <Route path="/admin/crm/members" element={<ProtectedRoute><CRMMembers /></ProtectedRoute>} />
        <Route path="/admin/crm/flexipay" element={<ProtectedRoute><FlexiPay /></ProtectedRoute>} />
        <Route path="/admin/crm/flexipay-playbook" element={<ProtectedRoute><FlexiPayPlaybook /></ProtectedRoute>} />
        <Route path="/admin/crm/followups" element={<ProtectedRoute><FollowUpCenter /></ProtectedRoute>} />
        <Route path="/admin/crm/playbooks" element={<ProtectedRoute><Playbooks /></ProtectedRoute>} />
        <Route path="/admin/crm/training-decks" element={<ProtectedRoute><TrainingDecks /></ProtectedRoute>} />
        <Route path="/admin/crm/outstanding-payments" element={<ProtectedRoute><OutstandingPayments /></ProtectedRoute>} />
        <Route path="/admin/macrobeast" element={<ProtectedRoute><MacroBeastDashboard /></ProtectedRoute>} />
        <Route path="/admin/macrobeast/subscriptions" element={<ProtectedRoute><MacroBeastSubscriptions /></ProtectedRoute>} />
        <Route path="/admin/macrobeast/pos" element={<ProtectedRoute><MacroBeastPOS /></ProtectedRoute>} />
        <Route path="/admin/macrobeast/expenses" element={<ProtectedRoute><MacroBeastExpenses /></ProtectedRoute>} />
        <Route path="/admin/crm/campaigns" element={<ProtectedRoute><CRMCampaigns /></ProtectedRoute>} />
        <Route path="/admin/crm/lead-database" element={<ProtectedRoute><CRMLeadDatabase /></ProtectedRoute>} />
        <Route path="/admin/crm/products" element={<ProtectedRoute><CRMProducts /></ProtectedRoute>} />
        <Route path="/admin/crm/coaches" element={<ProtectedRoute><CRMCoaches /></ProtectedRoute>} />
        <Route path="/admin/crm/employees" element={<ProtectedRoute><CRMEmployees /></ProtectedRoute>} />
        <Route path="/admin/crm/links" element={<ProtectedRoute><CRMLinks /></ProtectedRoute>} />
        <Route path="/admin/crm/attendance" element={<ProtectedRoute><CRMAttendance /></ProtectedRoute>} />
        <Route path="/admin/crm/pt-schedule" element={<ProtectedRoute><CRMPTSchedule /></ProtectedRoute>} />
        <Route path="/admin/hr" element={<ProtectedRoute><HRModule /></ProtectedRoute>} />
        <Route path="/admin/hr/calendar" element={<ProtectedRoute><HRCalendar /></ProtectedRoute>} />
        <Route path="/admin/my-portal" element={<ProtectedRoute><MyPortal /></ProtectedRoute>} />
        <Route path="/admin/payroll" element={<ProtectedRoute><Payroll /></ProtectedRoute>} />
        <Route path="/admin/financials" element={<ProtectedRoute><Financials /></ProtectedRoute>} />
        <Route path="/admin/reconciliation" element={<ProtectedRoute><Reconciliation /></ProtectedRoute>} />
        <Route path="/admin/tax-payroll" element={<ProtectedRoute><TaxPayroll /></ProtectedRoute>} />
        <Route path="/admin/operator-intelligence" element={<ProtectedRoute><OperatorIntelligence /></ProtectedRoute>} />
        <Route path="/admin/analytics/customers" element={<ProtectedRoute><CustomerAnalytics /></ProtectedRoute>} />
        <Route path="/admin/events" element={<ProtectedRoute><Events /></ProtectedRoute>} />
        <Route path="/admin/feedback" element={<ProtectedRoute><CustomerFeedback /></ProtectedRoute>} />
        <Route path="/admin/icebath" element={<ProtectedRoute><IceBath /></ProtectedRoute>} />
        <Route path="/admin/purchase-analytics" element={<ProtectedRoute><PurchaseAnalytics /></ProtectedRoute>} />
        <Route path="/admin/inventory" element={<ProtectedRoute><Inventory /></ProtectedRoute>} />
        <Route path="/admin/advanced-analytics" element={<ProtectedRoute><AdvancedAnalytics /></ProtectedRoute>} />
        <Route path="/admin/speed-to-lead" element={<ProtectedRoute><SpeedToLead /></ProtectedRoute>} />
        <Route path="/admin/churn-management" element={<ProtectedRoute><ChurnManagement /></ProtectedRoute>} />
        <Route path="/admin/phone-audit" element={<ProtectedRoute><PhoneAudit /></ProtectedRoute>} />
        <Route path="/admin/data-health" element={<ProtectedRoute><DataHealthCheck /></ProtectedRoute>} />
        <Route path="/admin/settings/email" element={<ProtectedRoute><EmailAutomations /></ProtectedRoute>} />
        <Route path="/admin/settings/2fa" element={<ProtectedRoute><TwoFactorSetup /></ProtectedRoute>} />
        <Route path="/admin/help" element={<ProtectedRoute><Help /></ProtectedRoute>} />
        <Route path="/admin/checkin" element={<ProtectedRoute><EmployeeCheckin /></ProtectedRoute>} />
        <Route path="/admin/leave/my-leaves" element={<ProtectedRoute><MyLeaves /></ProtectedRoute>} />
        <Route path="/admin/leave/team-requests" element={<ProtectedRoute><TeamLeaveRequests /></ProtectedRoute>} />
        <Route path="/admin/hr/attendance-comparison" element={<ProtectedRoute><AttendanceComparison /></ProtectedRoute>} />
        <Route path="/admin/renewal-settings" element={<ProtectedRoute><RenewalSettings /></ProtectedRoute>} />
        <Route path="/admin/snake-pit" element={<ProtectedRoute><SnakePit /></ProtectedRoute>} />
        <Route path="/admin/fight-calendar" element={<ProtectedRoute><FightCalendarAdmin /></ProtectedRoute>} />
      </Routes>
    );
  }

  // Landing pages and Thank You pages get minimal chrome (no navbar/footer)
  const isLandingPage = location.pathname.startsWith('/landing/');
  const isThankYouPage = location.pathname.startsWith('/thank-you/');
  const isWaiverPage = location.pathname === '/waiver';
  const isTabletPage = location.pathname === '/tablet';
  const isFeedbackPage = location.pathname.startsWith('/feedback/');
  const isCoachFeedbackPage = location.pathname.startsWith('/coach-feedback/');

  if (isLandingPage) {
    return (
      <Routes>
        <Route path="/landing/:slug" element={<LandingPage />} />
      </Routes>
    );
  }

  if (isThankYouPage) {
    return (
      <Routes>
        <Route path="/thank-you/:slug" element={<ThankYouPage />} />
      </Routes>
    );
  }

  // Customer waiver page - full screen, no navbar (OLD - customer types)
  if (isWaiverPage) {
    return (
      <Routes>
        <Route path="/waiver" element={<CustomerWaiver />} />
      </Routes>
    );
  }

  // Tablet display - staff-controlled display for front desk
  if (isTabletPage) {
    return (
      <Routes>
        <Route path="/tablet" element={<TabletDisplay />} />
      </Routes>
    );
  }

  // Customer feedback form - public, no navbar
  if (isFeedbackPage) {
    return (
      <Routes>
        <Route path="/feedback/:token" element={<FeedbackForm />} />
      </Routes>
    );
  }

  // Coach feedback form - public, no navbar
  if (isCoachFeedbackPage) {
    return (
      <Routes>
        <Route path="/coach-feedback/:token" element={<CoachFeedbackForm />} />
      </Routes>
    );
  }

  // Snake Pit public page - standalone dark theme
  const isSnakePitPage = location.pathname.startsWith('/snake-pit');
  if (isSnakePitPage) {
    return (
      <Routes>
        <Route path="/snake-pit" element={<SnakePitEventList />} />
        <Route path="/snake-pit/:eventId" element={<SnakePitEventDetail />} />
      </Routes>
    );
  }

  // Public India-wide Fight Calendar - standalone dark theme
  const isFightCalendarPage = location.pathname.startsWith('/fight-calendar');
  if (isFightCalendarPage) {
    return (
      <Routes>
        <Route path="/fight-calendar" element={<FightCalendarPublic />} />
      </Routes>
    );
  }

  // Founding 100 — Sopan Baug launch ad landing page, standalone premium theme
  const isFoundingPage = location.pathname === '/founding-100' || location.pathname === '/sopan-baug';
  if (isFoundingPage) {
    return (
      <Routes>
        <Route path="/founding-100" element={<FoundingHundred />} />
        <Route path="/sopan-baug" element={<FoundingHundred />} />
      </Routes>
    );
  }

  // Paid-ad acquisition funnels — standalone, no site chrome, single conversion goal
  const isFunnelThankYou =
    location.pathname === '/fat-loss/thank-you' || location.pathname === '/kesnand/thank-you';
  if (isFunnelThankYou) {
    return (
      <Routes>
        <Route path="/fat-loss/thank-you" element={<FunnelThankYou />} />
        <Route path="/kesnand/thank-you" element={<FunnelThankYou />} />
      </Routes>
    );
  }
  const isKesnandFunnel = location.pathname === '/kesnand';
  if (isKesnandFunnel) {
    return (
      <Routes>
        <Route path="/kesnand" element={<CobraThaiKesnand />} />
      </Routes>
    );
  }
  const isFatLossFunnel = location.pathname === '/fat-loss';
  if (isFatLossFunnel) {
    return (
      <Routes>
        <Route path="/fat-loss" element={<CobraThaiFatLoss />} />
      </Routes>
    );
  }

  if (location.pathname.startsWith('/unsubscribe/')) {
    return (
      <Routes>
        <Route path="/unsubscribe/:token" element={<Unsubscribe />} />
      </Routes>
    );
  }

  // PCMC strategic proposal - standalone dark theme, private slug
  const isPCMCPage = location.pathname === '/pcmc-strategic-proposal-9f7a2c';
  if (isPCMCPage) {
    return (
      <Routes>
        <Route path="/pcmc-strategic-proposal-9f7a2c" element={<PCMCProposal />} />
      </Routes>
    );
  }

  // Cobra Thai Camera Confidence & Content Performance Programme - standalone L&D page
  const isCameraConfidencePage = location.pathname === '/coach-camera-confidence-programme' || location.pathname === '/camera-ready';
  if (isCameraConfidencePage) {
    return (
      <Routes>
        <Route path="/coach-camera-confidence-programme" element={<CameraConfidence />} />
        <Route path="/camera-ready" element={<CameraConfidence />} />
      </Routes>
    );
  }

  // Private investor briefing page - dynamic slug
  const isInvestorPage = slugLoaded && location.pathname === `/${investorSlug}`;
  if (isInvestorPage) {
    return (
      <Routes>
        <Route path={`/${investorSlug}`} element={<InvestorPitch />} />
      </Routes>
    );
  }

  return (
    <>
      <Navbar />
      <MarketingSEO />
      <main className="pt-20">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/membership" element={<Membership />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/elite-residency" element={<EliteResidency />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/s/:slug" element={<SEOPage />} />
          <Route path="/services/*" element={<BrightLabsCmsPage basePath="/services" />} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <BrowserRouter>
        <TrackingScripts />
        <ScrollToTop />
        <Suspense fallback={<PageLoader />}>
          <AppContent />
        </Suspense>
        <Toaster position="top-right" richColors duration={3000} />
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
